import type { AppRole } from "./auth";
import { logAdminAction } from "./auditLog";
import { supabase } from "./supabase";

export type AnnouncementChannel = "in_app" | "email" | "both";
export type AnnouncementAudienceKind = "all" | "role" | "competition";

export type AnnouncementRow = {
  id: string;
  title: string;
  body: string | null;
  channel: AnnouncementChannel;
  audience_kind: AnnouncementAudienceKind;
  audience_role: string | null;
  audience_competition_id: string | null;
  publish_at: string | null;
  published_at: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string | null;
};

export type MyAnnouncement = AnnouncementRow & {
  /** False until the user has opened the announcement detail (server-tracked). */
  viewed: boolean;
};

export type CreateAnnouncementInput = {
  title: string;
  body: string;
  channel: AnnouncementChannel;
  audience_kind: AnnouncementAudienceKind;
  audience_role?: AppRole | string | null;
  audience_competition_id?: string | null;
  publish_at?: string | null;
};

export type AnnouncementPatch = Partial<{
  title: string;
  body: string | null;
  channel: AnnouncementChannel;
  audience_kind: AnnouncementAudienceKind;
  audience_role: string | null;
  audience_competition_id: string | null;
  publish_at: string | null;
}>;

function coerceChannel(v: unknown): AnnouncementChannel {
  if (v === "in_app" || v === "email" || v === "both") return v;
  return "in_app";
}

function coerceAudienceKind(v: unknown): AnnouncementAudienceKind {
  if (v === "all" || v === "role" || v === "competition") return v;
  return "all";
}

function mapAnnouncementRow(raw: Record<string, unknown>): AnnouncementRow {
  return {
    id: String(raw.id ?? ""),
    title: String(raw.title ?? ""),
    body: (raw.body as string | null | undefined) ?? null,
    channel: coerceChannel(raw.channel),
    audience_kind: coerceAudienceKind(raw.audience_kind),
    audience_role: (raw.audience_role as string | null | undefined) ?? null,
    audience_competition_id: (raw.audience_competition_id as string | null | undefined) ?? null,
    publish_at: (raw.publish_at as string | null | undefined) ?? null,
    published_at: (raw.published_at as string | null | undefined) ?? null,
    created_by: (raw.created_by as string | null | undefined) ?? null,
    created_at: String(raw.created_at ?? ""),
    updated_at: (raw.updated_at as string | null | undefined) ?? null,
  };
}

function unwrapRpcRows<T>(data: unknown): T[] {
  if (data == null) return [];
  if (Array.isArray(data)) return data as T[];
  return [];
}

/** User feed: published announcements visible to the current user, newest first. */
export async function listMyAnnouncements(): Promise<MyAnnouncement[]> {
  const { data, error } = await supabase.rpc("list_my_announcements");
  if (error) throw new Error(error.message);
  const rows = unwrapRpcRows<Record<string, unknown>>(data);
  const out: MyAnnouncement[] = rows.map((r) => {
    const base = mapAnnouncementRow(r);
    const viewed = Boolean(r.viewed ?? r.is_viewed ?? r.has_viewed);
    return { ...base, viewed };
  });
  out.sort((a, b) => {
    const ta = new Date(a.published_at ?? a.created_at).getTime();
    const tb = new Date(b.published_at ?? b.created_at).getTime();
    return tb - ta;
  });
  return out;
}

/** Admin: all announcements (drafts, scheduled, published). */
export async function listAllAnnouncements(): Promise<AnnouncementRow[]> {
  const { data, error } = await supabase
    .from("announcements")
    .select("*")
    .order("created_at", { ascending: false });
  if (error) throw new Error(error.message);
  return (data ?? []).map((r) => mapAnnouncementRow(r as Record<string, unknown>));
}

export async function createAnnouncement(input: CreateAnnouncementInput): Promise<string> {
  const {
    data: { user },
  } = await supabase.auth.getUser();
  const audience_role = input.audience_kind === "role" ? (input.audience_role ?? null) : null;
  const audience_competition_id =
    input.audience_kind === "competition" ? (input.audience_competition_id ?? null) : null;

  const { data, error } = await supabase
    .from("announcements")
    .insert({
      title: input.title.trim(),
      body: input.body?.trim() ? input.body.trim() : null,
      channel: input.channel,
      audience_kind: input.audience_kind,
      audience_role,
      audience_competition_id,
      publish_at: input.publish_at?.trim() ? input.publish_at.trim() : null,
      created_by: user?.id ?? null,
    })
    .select("id")
    .single();

  if (error) throw new Error(error.message);
  const id = data?.id;
  if (!id) throw new Error("No id returned");
  await logAdminAction({
    action: "announcement.create",
    entityType: "announcements",
    entityId: String(id),
    summary: `Created announcement “${input.title.trim()}”`,
    diff: {
      after: {
        title: input.title.trim(),
        channel: input.channel,
        audience_kind: input.audience_kind,
      },
    },
  });
  return String(id);
}

export async function updateAnnouncement(id: string, patch: AnnouncementPatch): Promise<void> {
  const row: Record<string, unknown> = {};
  if (patch.title !== undefined) row.title = patch.title.trim();
  if (patch.body !== undefined) row.body = patch.body?.trim() ? patch.body.trim() : null;
  if (patch.channel !== undefined) row.channel = patch.channel;
  if (patch.audience_kind !== undefined) row.audience_kind = patch.audience_kind;
  if (patch.audience_role !== undefined) row.audience_role = patch.audience_role;
  if (patch.audience_competition_id !== undefined) row.audience_competition_id = patch.audience_competition_id;
  if (patch.publish_at !== undefined) row.publish_at = patch.publish_at?.trim() ? patch.publish_at.trim() : null;

  const { error } = await supabase.from("announcements").update(row).eq("id", id);
  if (error) throw new Error(error.message);
}

export async function deleteAnnouncement(id: string): Promise<void> {
  const { error } = await supabase.from("announcements").delete().eq("id", id);
  if (error) throw new Error(error.message);
  await logAdminAction({
    action: "announcement.delete",
    entityType: "announcements",
    entityId: id,
    summary: `Deleted announcement ${id.slice(0, 8)}…`,
    diff: { before: { id } },
  });
}

export async function publishAnnouncement(id: string): Promise<void> {
  const { error } = await supabase.rpc("publish_announcement", { p_announcement_id: id });
  if (error) throw new Error(error.message);
}

export async function markAnnouncementViewed(id: string): Promise<void> {
  const { error } = await supabase.rpc("mark_announcement_viewed", { p_announcement_id: id });
  if (error) throw new Error(error.message);
}

export function announcementStatus(row: Pick<AnnouncementRow, "published_at" | "publish_at">): "draft" | "scheduled" | "published" {
  if (row.published_at) return "published";
  if (row.publish_at && new Date(row.publish_at) > new Date()) return "scheduled";
  return "draft";
}

/**
 * Captain-only path: post a competition-scoped announcement that auto-
 * publishes immediately. Backed by the SECURITY DEFINER RPC which checks
 * that the caller is the captain of a team in `competitionId` — admins
 * use the direct insert (createAnnouncement) instead.
 */
export async function captainSendAnnouncement(
  competitionId: string,
  title: string,
  body: string
): Promise<string> {
  const { data, error } = await supabase.rpc("captain_send_announcement", {
    p_competition_id: competitionId,
    p_title: title,
    p_body: body,
  });
  if (error) throw new Error(error.message);
  if (!data) throw new Error("No id returned");
  return String(data);
}

/**
 * Captain-only path: hard-delete a competition-scoped announcement. Any
 * captain of a team in the announcement's competition can remove it —
 * the RPC enforces that server-side. Removing the row makes it disappear
 * from team chat, the program-scoped overlay, and the activity bell on
 * the next refresh, since all three surfaces re-derive from the table.
 */
export async function captainDeleteAnnouncement(announcementId: string): Promise<void> {
  const { error } = await supabase.rpc("captain_delete_announcement", {
    p_announcement_id: announcementId,
  });
  if (error) throw new Error(error.message);
}
